// src/pages/ReviewProfile.js
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import WorkerSidebar from "../components/WorkerSidebar";
import ProgressTracker from "../components/ProgressTracker";

const API_WRK = process.env.REACT_APP_API_WRK;
// Derive API host (e.g., http://localhost:5000) from http://.../api/worker
const API_HOST =
  API_WRK?.replace(/\/api\/worker\/?$/, "") || window.location.origin;

const ReviewProfile = () => {
  const [workerData, setWorkerData] = useState(null);
  const [showSidebar, setShowSidebar] = useState(false);
  const navigate = useNavigate();

  const workerId = localStorage.getItem("workerId");

  // ✅ Redirect if not logged in
  useEffect(() => {
    if (!workerId) {
      navigate("/worker-login");
      return;
    }

    const fetchWorker = async () => {
      try {
        const res = await axios.get(`${API_WRK}/profile/${workerId}`);
        setWorkerData(res.data);
      } catch (err) {
        console.error("Failed to fetch worker:", err);
        navigate("/worker-login");
      }
    };

    fetchWorker();
  }, [workerId, navigate]);

  const handleSubmit = async () => {
  try {
    const res = await axios.post(`${API_WRK}/submit-profile`, { workerId });

    if (res.data?.success) {
      localStorage.setItem("workerStatus", "pending");
      alert("✅ Your profile has been submitted for review!");
      navigate("/dashboard");
    } else {
      alert(res.data?.message || "❌ Submission failed. Please try again.");
    }
  } catch (err) {
    console.error("❌ Failed to submit profile:", err);
    alert(
      err?.response?.data?.error ||
        "Something went wrong while submitting. Please try again."
    );
  }
};

  const handleLogout = () => {
    localStorage.clear();
    navigate("/worker-login");
  };

  const isEditable = workerData?.status === "incomplete";
  const fullUrl = (url) =>
    url?.startsWith("http") ? url : `${API_HOST}${url}`;

  if (!workerId) return null;
  if (!workerData)
    return (
      <div className="p-6 text-center text-gray-700">Loading...</div>
    );

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-gray-100 to-white">
      <WorkerSidebar showSidebar={showSidebar} />

      <div className="flex-1 ml-0 md-ml-64 p-6 space-y-8">
        {/* ✅ Progress bar at top */}
        <ProgressTracker currentStep={5} />

        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-gray-800">
            Review Your Profile
          </h1>
          <button
            onClick={handleLogout}
            className="bg-red-500 text-white px-5 py-2 rounded-lg shadow hover:bg-red-600"
          >
            Logout
          </button>
        </div>

        {/* Personal Info */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-800">
              Personal Information
            </h2>
            {isEditable && (
              <button
                onClick={() => navigate("/worker-profile")}
                className="text-blue-600 hover:underline text-sm font-medium"
              >
                Edit Profile
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-3 text-gray-700 text-sm">
            <p>
              <strong>Name:</strong> {workerData.name}
            </p>
            <p>
              <strong>Phone:</strong> {workerData.phone}
            </p>
            <p>
              <strong>Address:</strong> {workerData.address}
            </p>
            <p>
              <strong>City:</strong> {workerData.city}
            </p>
            <p>
              <strong>Province:</strong> {workerData.province}
            </p>
            <p>
              <strong>Postal Code:</strong> {workerData.postalCode}
            </p>
          </div>
        </div>

        {/* IDs */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-800">
              Uploaded Identification
            </h2>
            {isEditable && (
              <button
                onClick={() => navigate("/upload-documents?from=review")}
                className="text-blue-600 hover:underline text-sm font-medium"
              >
                Edit Identification
              </button>
            )}
          </div>
          <ul className="text-sm text-gray-700 space-y-2">
            <li>
              <strong>{workerData.id1Type} (Front):</strong>{" "}
              <a
                href={fullUrl(workerData.id1FrontUrl)}
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 underline"
              >
                View
              </a>
            </li>
            <li>
              <strong>{workerData.id1Type} (Back):</strong>{" "}
              <a
                href={fullUrl(workerData.id1BackUrl)}
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 underline"
              >
                View
              </a>
            </li>
            <li>
              <strong>{workerData.id2Type} (Front):</strong>{" "}
              <a
                href={fullUrl(workerData.id2FrontUrl)}
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 underline"
              >
                View
              </a>
            </li>
            <li>
              <strong>{workerData.id2Type} (Back):</strong>{" "}
              <a
                href={fullUrl(workerData.id2BackUrl)}
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 underline"
              >
                View
              </a>
            </li>
            {workerData.selfieUrl && (
              <li>
                <strong>Selfie:</strong>{" "}
                <a
                  href={fullUrl(workerData.selfieUrl)}
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-600 underline"
                >
                  View
                </a>
              </li>
            )}
            {workerData.isInternational && workerData.permitUrl && (
              <li>
                <strong>Work Permit:</strong>{" "}
                <a
                  href={fullUrl(workerData.permitUrl)}
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-600 underline"
                >
                  View
                </a>
              </li>
            )}
          </ul>
        </div>

        {/* Services */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-800">
              Your Services & Certifications
            </h2>
            {isEditable && (
              <button
                onClick={() => navigate("/select-services?from=review")}
                className="text-blue-600 hover:underline text-sm font-medium"
              >
                Edit Services
              </button>
            )}
          </div>

          {workerData.services?.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {workerData.services.map((service, index) => (
                <div
                  key={index}
                  className="bg-gray-50 border rounded-lg p-4 shadow-sm text-sm"
                >
                  <p>
                    <strong>Service:</strong> {service.name}
                  </p>
                  <p>
                    <strong>Has Tools:</strong>{" "}
                    {service.hasTools ? "Yes" : "No"}
                  </p>
                  {service.name === "Moving Help" && (
                    <p>
                      <strong>Has Truck:</strong>{" "}
                      {service.hasTruck ? "Yes" : "No"}
                    </p>
                  )}
                  <p>
                    <strong>Certification:</strong>{" "}
                    {service.certUrl ? (
                      <a
                        href={fullUrl(service.certUrl)}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-600 underline"
                      >
                        View Certificate
                      </a>
                    ) : (
                      "Not Uploaded"
                    )}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">
              No services selected yet.
            </p>
          )}
        </div>

        {/* Submit */}
        {isEditable && (
          <div className="text-center mt-10">
            <button
              onClick={handleSubmit}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-3 rounded-lg text-lg font-semibold shadow"
            >
              Submit Profile for Review
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReviewProfile;
